import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Button, CircularProgress, MenuItem, Select, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Pagination, InputLabel, FormControl, Typography, IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { BASE_URL } from '../../App';
import Swal from 'sweetalert2';

const GlobalBillingMinvalueConfig = () => {
  const [billingGroups, setBillingGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editedRows, setEditedRows] = useState([]);
  const [editableRowIds, setEditableRowIds] = useState([]);
  const login_id = localStorage.getItem("LOGIN_ID");


  const [branchOptions, setBranchOptions] = useState([]);
  const [muUnitCodeOptions, setMuUnitCodeOptions] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState({
    branch_code: '',
    billing_group: '',
    muunitcode: '',
    NPO: '',
    XPO: '',
    MPO: '',
    branch_name: ''
  });

  const rowsPerPage = 10;

  useEffect(() => {
    axios.get(`${BASE_URL}/getBillingGroups`)
      .then((res) => setBillingGroups(res.data))
      .catch((err) => console.error('Error fetching billing groups:', err));

    axios.get(`${BASE_URL}/getBranchOptions`)
      .then((res) => setBranchOptions(res.data))
      .catch((err) => console.error('Error fetching branch options:', err));

    axios.get(`${BASE_URL}/getMuUnitCodeOptions`)
      .then((res) => setMuUnitCodeOptions(res.data))
      .catch((err) => console.error('Error fetching MU Unit Code options:', err));
  }, []);

  useEffect(() => {
    if (newRow.branch_code) {
      const selectedBranch = branchOptions.find(b => b.code === newRow.branch_code);
      if (selectedBranch) {
        setNewRow(prev => ({ ...prev, branch_name: selectedBranch.name }));
      }
    }
  }, [newRow.branch_code, branchOptions]);

  const handleViewData = () => {
    if (!selectedGroup) return;

    setLoading(true);
    axios.get(`${BASE_URL}/getBillingGroupValues`, {
      params: { billing_group: selectedGroup },
    })
      .then((res) => {
        setTableData(res.data);
        setFilteredData(res.data);
        setEditedRows([]);
        setEditableRowIds([]);
        setLoading(false);
        setCurrentPage(1);
      })
      .catch((err) => {
        console.error('Error fetching config data:', err);
        setLoading(false);
      });
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = tableData.filter(item =>
      String(item.branch_code).toLowerCase().includes(value)
    );
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleEdit = (index) => {
    setEditableRowIds(prev => [...prev, index]);
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...filteredData];
    updated[index][field] = value;
    setFilteredData(updated);

    const edited = [...editedRows];
    const existing = edited.find(row =>
      row.branch_code === updated[index].branch_code &&
      row.billing_group === updated[index].billing_group &&
      row.muunitcode === updated[index].muunitcode
    );

    if (existing) {
      existing[field] = value;
    } else {
      edited.push({
        branch_code: updated[index].branch_code,
        billing_group: updated[index].billing_group,
        muunitcode: updated[index].muunitcode,
        NPO: updated[index].NPO,
        XPO: updated[index].XPO,
        MPO: updated[index].MPO,
      });
    }
    setEditedRows(edited);
  };

  const handleUpdate = () => {
    // const login_id = localStorage.getItem("LOGIN_ID");
    if (editedRows.length === 0) return;

  axios.post(`${BASE_URL}/updateBillingGroupValues`, {
    updates: editedRows,
    login_id: login_id
  })
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Update successful!',
          confirmButtonColor: '#3085d6'
        });
        handleViewData();
      })
      .catch((err) => {
        console.error('Update failed:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Update failed!',
        });
      });
  };

  const handleAddChange = (field, value) => {
    setNewRow(prev => ({ ...prev, [field]: value }));
    
  };

  const handleAddRow = () => {
    if (!newRow.branch_code || !newRow.billing_group || !newRow.muunitcode) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill all mandatory fields!',
      });
      return;
    }

    axios.post(`${BASE_URL}/addBillingGroupValue`, newRow)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Row Added',
          text: 'New row added successfully!',
        });
        setIsAdding(false);
        setNewRow({
          branch_code: '',
          billing_group: '',
          muunitcode: '',
          NPO: '',
          XPO: '',
          MPO: '',
          branch_name: ''
        });
        handleViewData();
      })
      .catch((err) => {
        console.error('Add row failed:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to add row.',
        });
      });
  };

  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <Box p={3}>
      <FormControl sx={{ minWidth: 300, mb: 2 }} size="small">
        <InputLabel>Select Billing Group</InputLabel>
        <Select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          label="Select Billing Group"
        >
          {billingGroups.map((group) => (
            <MenuItem key={group.id} value={group.billing_group}>
              {group.billing_group}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box mt={1} mb={2}>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#81c784', mr: 2 }}
          onClick={handleViewData}
          disabled={!selectedGroup}
        >
          View
        </Button>

        <Button
          variant="contained"
          sx={{ backgroundColor: '#64b5f6' }}
          onClick={() => setIsAdding(true)}
        >
          Add New Row
        </Button>
      </Box>

         {editedRows.length > 0 && (
  <Box
    mt={5} // Increases top margin to move it down
    display="flex"
    justifyContent="flex-end" // Aligns button to the right
    pr={3} // Optional: adds right padding (you can adjust as needed)
  >
    <Button variant="contained" color="success" onClick={handleUpdate}>
      Update
    </Button>
  </Box>
)}

      {loading && <CircularProgress sx={{ display: 'block', my: 3 }} />}

      {filteredData.length > 0 || isAdding ? (
        <>
          <TextField
            label="Search by Branch Code"
            variant="outlined"
            size="small"
            sx={{ mb: 2, width: 300 }}
            value={searchTerm}
            onChange={handleSearch}
          />

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#e3f2fd' }}>
                <TableRow>
                  <TableCell><strong>Branch Code</strong></TableCell>
                  <TableCell><strong>Billing Group</strong></TableCell>
                  <TableCell><strong>MU Unit Code</strong></TableCell>
                  <TableCell><strong>NPO</strong></TableCell>
                  <TableCell><strong>XPO</strong></TableCell>
                  <TableCell><strong>MPO</strong></TableCell>
                  <TableCell><strong>Branch Name</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isAdding && (
                  <TableRow>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={newRow.branch_code}
                          onChange={(e) => handleAddChange('branch_code', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>Select Branch Code</MenuItem>
                          {branchOptions.map((branch) => (
                            <MenuItem key={branch.code} value={branch.code}>
                              {branch.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={newRow.billing_group}
                          onChange={(e) => handleAddChange('billing_group', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>Select Billing Group</MenuItem>
                          {billingGroups.map((group) => (
                            <MenuItem key={group.id} value={group.billing_group}>
                              {group.billing_group}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={newRow.muunitcode}
                          onChange={(e) => handleAddChange('muunitcode', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>Select MU Unit Code</MenuItem>
                          {muUnitCodeOptions.map((code, index) => (
                            <MenuItem key={index} value={code.muunitcode}>
                              {code.muunitcode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField 
                        size="small" 
                        fullWidth
                        value={newRow.NPO} 
                        onChange={(e) => handleAddChange('NPO', e.target.value)} 
                      />
                    </TableCell>
                    <TableCell>
                      <TextField 
                        size="small" 
                        fullWidth
                        value={newRow.XPO} 
                        onChange={(e) => handleAddChange('XPO', e.target.value)} 
                      />
                    </TableCell>
                    <TableCell>
                      <TextField 
                        size="small" 
                        fullWidth
                        value={newRow.MPO} 
                        onChange={(e) => handleAddChange('MPO', e.target.value)} 
                      />
                    </TableCell>
                    <TableCell>
                      <TextField 
                        size="small" 
                        fullWidth
                        value={newRow.branch_name} 
                        onChange={(e) => handleAddChange('branch_name', e.target.value)}
                        disabled
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" color="primary" variant="outlined" onClick={handleAddRow}>Save</Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => setIsAdding(false)} sx={{ ml: 1 }}>Cancel</Button>
                    </TableCell>
                  </TableRow>
                )}

                {paginatedData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.branch_code}</TableCell>
                    <TableCell>{item.billing_group}</TableCell>
                    <TableCell>{item.muunitcode}</TableCell>

                    {editableRowIds.includes(index) ? (
                      <>
                        <TableCell>
                          <TextField size="small" value={item.NPO} onChange={(e) => handleInputChange(index, 'NPO', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={item.XPO} onChange={(e) => handleInputChange(index, 'XPO', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={item.MPO} onChange={(e) => handleInputChange(index, 'MPO', e.target.value)} />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{item.NPO}</TableCell>
                        <TableCell>{item.XPO}</TableCell>
                        <TableCell>{item.MPO}</TableCell>
                      </>
                    )}

                    <TableCell>{item.branch_name}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(index)}>
                        <EditIcon color="primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(e, value) => setCurrentPage(value)}
              color="primary"
            />
          </Box>

        
        </>
      ) : null}
    </Box>
  );
};

export default GlobalBillingMinvalueConfig;